// Service to handle Employee related API calls
import {
  EmployeeApiResponse,
  EmployeeCheckUserGetResponse,
  EmployeeGetResponse,
  EmployeesGetResponse,
  PermissionsGetResponse,
  UpdateEmployeeBilledRateRequest,
  UpdateEmployeeBilledRateResponse,
  UpdateEmployeePermishRequest,
  UpdateEmployeePermishResponse,
  UpdateEmployeeRatesRequest,
  UpdateEmployeeRatesResponse,
  CreateEmployeeRequest,
  CreateEmployeeResponse,
  CreateFieldMemberRequest,
  CreateFieldMemberResponse,
  CreatePermissionRequest,
  EnableSigninResponse,
  CreatePermissionResponse,
  Permission,
} from '../../Models/Employee/EmployeeRequestResponseModels';
import HttpService from './HttpService';
import {
  pathCheckUserByOid,
  pathEmployeeIdPermish,
  pathEmployeeIdRates,
  pathEmployees,
  pathGetEmployee,
  pathPermissions,
  pathCreateEmployee,
  pathCreateFieldMember,
  pathEmployeeId,
  pathEnableSignin,
} from '../../__settings__/septSoftwarePaths';

const http = new HttpService();

class EmployeeService {
  /**
   * This checks the user by the oid
   * @param oid the oid to check
   * @returns
   */
  static async checkUser(oid: string): Promise<EmployeeCheckUserGetResponse> {
    const response = await http.get<EmployeeApiResponse<EmployeeCheckUserGetResponse>>(pathCheckUserByOid(oid));
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while checking user.');
    }
    return response.data;
  }

  // Get all employees
  static async getEmployees(): Promise<EmployeesGetResponse> {
    const response = await http.get<EmployeeApiResponse<EmployeesGetResponse>>(pathEmployees);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching employees.');
    }
    return response.data.sort((a, b) => a.EmployeeID.localeCompare(b.EmployeeID));
  }

  // Update employee based on Employee ID
  static async updateEmployee(employeeId: string,
    updatedEmployee: UpdateEmployeeBilledRateRequest): Promise<UpdateEmployeeBilledRateResponse> {
      const response = await http.put<EmployeeApiResponse<UpdateEmployeeBilledRateResponse>>(pathEmployeeId(employeeId), updatedEmployee);
      if (!response.successful || !response.data) {
        throw new Error(response.error || 'An error occurred while updating employee.');
      }
      return response.data;
  }

  // Get employee based on Employee ID
  static async getEmployeeById(employeeId: string): Promise<EmployeeGetResponse | null> {
    const response = await http.get<EmployeeApiResponse<EmployeeGetResponse>>(pathGetEmployee(employeeId));
    if (!response.successful || !response.data) {
      return null;
    }
    return response.data;
  }

  // Update billed rate based on Employee ID
  static async updateBilledRate(employeeId: string, newBilledRate: number): Promise<UpdateEmployeeBilledRateResponse> {
    const update: UpdateEmployeeBilledRateRequest = { billedrate: newBilledRate };
    const response = await http.put<EmployeeApiResponse<UpdateEmployeeBilledRateResponse>>(pathGetEmployee(employeeId), update);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while updating billed rate.');
    }
    return response.data;
  }

  // Update employee permission ID
  static async updateEmployeePermish(employeeId: string, newPermishId: number): Promise<UpdateEmployeePermishResponse> {
    const update: UpdateEmployeePermishRequest = { permish_id: newPermishId };
    const response = await http.put<EmployeeApiResponse<UpdateEmployeePermishResponse>>(pathEmployeeIdPermish(employeeId), update);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while updating permission.');
    }
    return response.data;
  }

  // Get all permissions
  static async getPermissions(): Promise<Permission[]> {
    const response = await http.get<EmployeeApiResponse<PermissionsGetResponse>>(pathPermissions);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching permissions.');
    }
    return response.data;
  }

  // Create a FIELD MEMBER (Build M): assignable to teams and phases, cannot sign in. The id
  // is minted server-side — the request deliberately carries none.
  static async createFieldMember(member: CreateFieldMemberRequest): Promise<CreateFieldMemberResponse> {
    const response = await http.post<EmployeeApiResponse<CreateFieldMemberResponse>>(
      pathCreateFieldMember, member);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while creating the field member.');
    }
    return response.data;
  }

  // Enable sign-in for a FIELD MEMBER (Build C): links the Entra oid to the SAME row —
  // the member's employeeId never changes, so their whole history survives.
  static async enableSignin(employeeId: string, entraOid: string, permishId: number): Promise<EnableSigninResponse> {
    const response = await http.post<EmployeeApiResponse<EnableSigninResponse>>(
      pathEnableSignin(employeeId), { entraOid, permishId });
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while enabling sign-in.');
    }
    return response.data;
  }

  // Create a new employee
  static async createEmployee(employee: CreateEmployeeRequest): Promise<CreateEmployeeResponse> {
    const response = await http.post<EmployeeApiResponse<CreateEmployeeResponse>>(pathCreateEmployee, employee);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while creating employee.');
    }
    return response.data;
  }

  // Create a new permission
  static async createPermission(permission: CreatePermissionRequest): Promise<CreatePermissionResponse> {
    const response = await http.post<EmployeeApiResponse<CreatePermissionResponse>>(pathPermissions, permission);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while creating permission.');
    }
    return response.data;
  }

  // Update employee rate associations
  static async updateEmployeeRates(
    employeeId: string,
    rates: UpdateEmployeeRatesRequest
  ): Promise<UpdateEmployeeRatesResponse> {
    const data = await http.put<UpdateEmployeeRatesResponse>(
      pathEmployeeIdRates(employeeId),
      rates
    );
    return data;
  }
}

export default EmployeeService;
